/**
 * Types for the bulk-upload modal: the request and response of the Items API's
 * `validate-item-part-numbers` route (`web-items-api`
 * `functions/PostPublicValidateItemPartNumbersV1/postPublicValidateItemPartNumbers.ts`), the saved
 * field mapping of the Users API (`web-users-api` `UserApi.BulkUploadFieldMappings`), the column
 * mapping, and the props `BulkUploadModal.tsx` passes to `BulkUploadModalDesktop.tsx`.
 *
 * The request and response shapes are a contract with `web-items-api`; they are declared here, not
 * taken from `@ryerson/common.service-contracts`.
 */
import React from "react";
import { Language, Product } from "@ryerson/frontend.common";
import { StepperDetail } from "@ryerson/frontend.progress";
import { BulkUploadModalText } from "./BulkUploadModalContent";
/**
 * One spreadsheet row as sent for validation. Every field is sent as a string. The Items API
 * validator (`PartNumberRequestValidator`) runs `notEmpty` on `id`, `partNumber`, `quantity` and
 * `quantityUom`, and fails the whole request with a 500 when one of those arrives as a number.
 */
export type PartValidation = {
	/** The row's 0-based position in the file, as text. Shown to the customer as `id + 1`. */
	id: string;
	/** What the customer typed: a Ryerson SKU or their own customer part number. */
	partNumber: string;
	quantity: string;
	/**
	 * The file's text upper-cased (`LB`, `LBS`, `PC(S)`...), or a `DefaultUOMDisplay` key after a
	 * correction; the Items API normalises it. See {@link PartValidationRes} for the way back.
	 */
	quantityUom: string;
	/** Optional; the Items API fills a blank one with the product's default pricing UOM. */
	pricingUom: string;
	customerReferenceNumber: string;
	lineLevelPurchaseOrderNumber: string;
	lineItemNotes: string;
	/** Cart and quote uploads only. */
	requestedDeliveryDate: string;
};

/**
 * One row of the validation response, plus the fields this UI adds for the review step.
 *
 * After `BulkUploadModal.tsx` → `onSuccessBulkValidate`, `quantityUom` and `pricingUom` hold
 * display values from `@ryerson/common.uom` `DefaultUOMDisplay` (`lbs`, `pc(s)`), not codes.
 */
export interface PartValidationRes extends PartValidation {
	valid: boolean;
	/**
	 * Translated text for the errors this UI knows (`BulkUploadHelpers.tsx` → `mapErrorMessage`);
	 * otherwise the API's own text, untranslated.
	 */
	errorMessage: string | null;
	/**
	 * The field to flag, as one of this UI's keys (`partNumber`, `quantity`, ...) after
	 * `mapErrorName`.
	 */
	errorField: string | null;
	/**
	 * Set by the API on valid rows; for those, `partNumber` is the customer part number or
	 * `""`.
	 */
	ryersonSku: string | null;
	/** True once the customer has edited the row on the review step. Not sent to the API. */
	updatedByUser: boolean;
	/** Product found by the review step's search, used for the UOM choices. Not sent to the API. */
	productDetails?: Product | null;
}

/**
 * Body of `POST APIUrl.BULK_UPLOAD`. `account` is never set here; the API takes it from the
 * token.
 */
export type BulkUploadValidationRequest = {
	zipCode: string;
	language: Language;
	account?: string;
	parts: PartValidation[];
};
/**
 * Response of `POST APIUrl.BULK_UPLOAD`. A bad row does not fail the request; it comes back with
 * `valid: false`. The Items API builds `{ language, parts }`; `account` is declared but not sent.
 */
export type BulkUploadValidationResponse = {
	language: Language;
	account: string;
	parts: PartValidationRes[];
};

/** Props of `BulkUploadModalDesktop.tsx`: the state and handlers owned by `BulkUploadModal.tsx`. */
export type BulkUploadModalType = {
	content: BulkUploadModalText;
	testingId?: string;
	showBulkUpload: boolean;
	setShowBulkUpload: React.Dispatch<React.SetStateAction<boolean>>;
	bulkUploadStep: number;
	steps: StepperDetail[];
	fileMapping: FileMapping[];
	isMissingRequiredData: boolean;
	onSubmit: () => void;
	disabledSubmitButton: boolean;
	handleDropdownSelection: (newValue: string, fileField: FileMapping) => void;
	submittedFileInput: boolean;
	bulkUploadFiles: File[];
	setBulkUploadFiles: React.Dispatch<React.SetStateAction<File[]>>;
	fileFields: string[];
	buttonText: string;
	validFile: boolean;
	displayCancelUpload: boolean;
	setDisplayCancelUpload: (value: boolean) => void;
	validatedData: PartValidationRes[];
	setValidatedData: React.Dispatch<React.SetStateAction<PartValidationRes[]>>;
	processCorrections: boolean;
	setProcessCorrections: React.Dispatch<React.SetStateAction<boolean>>;
	invalidData: PartValidationRes[];
	setInvalidData: React.Dispatch<React.SetStateAction<PartValidationRes[]>>;
	invalidDataRef: React.MutableRefObject<PartValidationRes[]>;
	bulkUploadType: BulkUploadType;
	isGenericError: boolean;
	saveMapping: boolean;
	setSaveMapping: React.Dispatch<React.SetStateAction<boolean>>;
	isLoggedIn: boolean;
};

/**
 * maps ryerson’s columns to
 * user’s files’ columns
 *
 * One row of the map-fields step. The list is built in `BulkUploadModal.tsx` → `defaultFileData`.
 */
export type FileMapping = {
	/** 1-based sequence matching the list order in `defaultFileData`; not read by any code. */
	id: number;
	/** The file's header text chosen for this field; `""` while unmapped. */
	userColumn: string;
	/** The field's label in the current language (`MapFieldsContent.ts`). */
	ryersonColumn: string;
	/** The field's key in {@link PartValidation} (`partNumber`, `quantityUom`, ...). */
	value: string;
	/** The step cannot be left while a required field is unmapped. */
	required: boolean;
};

/** Where the uploaded rows go. Chooses the fields offered and the wording of the buttons. */
export enum BulkUploadType {
	shoppingCart = "shoppingCart",
	shoppingList = "shoppingList",
	quote = "quote",
}

/** A parsed sheet: one object per row, keyed by header text (or `Column A`... without headers). */
export type WorkBookJson = { [key: string]: string | number }[];

/**
 * A saved mapping from `GET APIUrl.BULK_UPLOAD_FIELD_MAPPING`: for each field, the header text of
 * the customer's file. `{}` when nothing is saved. Two keys differ from {@link PartValidation}:
 * `lineLevelPurchaseOrder` and `futureDeliveryDate`.
 */
export interface FieldMappingResponse {
	partNumber?: string;
	quantity?: string;
	quantityUom?: string;
	pricingUom?: string;
	customerReferenceNumber?: string;
	lineLevelPurchaseOrder?: string;
	lineItemNotes?: string;
	futureDeliveryDate?: string;
}
